import { useState, type ReactNode } from "react";
import { cn } from "@/lib/utils";

/**
 * v2.9 P2.2: the Notes panel's filter chips — Mine (the default: what a person wrote) · Calls (the call log,
 * inline) · System (ai_analysis, the rule notes, the curative brief) · All — each with its count. Pinned
 * notes come first (the API orders them). The add box stays above this, outside it.
 */
export type NotesChip = "mine" | "calls" | "system" | "all";

const LABEL: Record<NotesChip, string> = { mine: "Mine", calls: "Calls", system: "System", all: "All" };

export function NotesFeed<N extends { id: string; note_class?: string | null }>({
  notes,
  callCount,
  renderNote,
  renderCalls,
  empty,
}: {
  notes: readonly N[];
  callCount: number;
  renderNote: (note: N) => ReactNode;
  renderCalls: () => ReactNode;
  empty?: string;
}) {
  const [chip, setChip] = useState<NotesChip>("mine");
  const human = notes.filter((n) => n.note_class !== "system");
  const system = notes.filter((n) => n.note_class === "system");
  const counts: Record<NotesChip, number> = {
    mine: human.length,
    calls: callCount,
    system: system.length,
    all: notes.length + callCount,
  };
  const shown = chip === "mine" ? human : chip === "system" ? system : chip === "all" ? notes : [];
  return (
    <div>
      <div className="mb-2 flex flex-wrap gap-1" role="tablist" aria-label="Which notes">
        {(Object.keys(LABEL) as NotesChip[]).map((key) => (
          <button
            key={key}
            type="button"
            role="tab"
            aria-selected={chip === key}
            data-testid={`notes-chip-${key}`}
            onClick={() => setChip(key)}
            className={cn(
              "rounded-full border px-2 py-0.5 text-[11px]",
              chip === key ? "border-accent bg-accent/15 text-accent" : "text-fg-muted hover:bg-surface-2",
            )}
          >
            {LABEL[key]} <span className="tabular-nums" data-testid={`notes-chip-${key}-count`}>{counts[key]}</span>
          </button>
        ))}
      </div>
      {chip !== "calls" && (
        <ul className="space-y-1 text-xs" data-testid="notes-list">
          {shown.map((n) => renderNote(n))}
          {shown.length === 0 && <li className="text-fg-muted">{empty ?? "No notes here."}</li>}
        </ul>
      )}
      {(chip === "calls" || chip === "all") && (
        <div className={cn(chip === "all" && "mt-2 border-t pt-2")} data-testid="notes-calls">
          {renderCalls()}
        </div>
      )}
    </div>
  );
}
